import type { ReactNode } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router';

import { LiveEventsProvider } from '../features/events/LiveEvents';
import { RequireAuth } from '../features/auth/RequireAuth';
import { RequirePermission } from '../features/auth/RequirePermission';
import { LivePage } from '../pages/LivePage';
import { MediaNodePage } from '../pages/MediaNodePage';
import { MediaNodesPage } from '../pages/MediaNodesPage';
import { MonitoringPage } from '../pages/MonitoringPage';
import { MosaicsPage } from '../pages/MosaicsPage';
import { MosaicWindowPage } from '../pages/MosaicWindowPage';
import { AcceptInvitePage } from '../pages/AcceptInvitePage';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage';
import { LoginPage } from '../pages/LoginPage';
import { ResetPasswordPage } from '../pages/ResetPasswordPage';
import { ModulePage } from '../pages/ModulePage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { EvidencePage } from '../pages/EvidencePage';
import { EventsPage } from '../pages/EventsPage';
import { PlatformDashboardPage } from '../pages/PlatformDashboardPage';
import { PlaybackPage } from '../pages/PlaybackPage';
import { AuditPage } from '../pages/AuditPage';
import { DevicesPage } from '../pages/DevicesPage';
import { IntegrationsPage } from '../pages/IntegrationsPage';
import { ClientsPage } from '../pages/ClientsPage';
import { RolesPage } from '../pages/RolesPage';
import { SettingsPage } from '../pages/SettingsPage';
import { StoragePage } from '../pages/StoragePage';
import { TenantsPage } from '../pages/TenantsPage';
import { UsersPage } from '../pages/UsersPage';
import { AppShell } from './AppShell';
import { Home, NotOnPlatform, SystemMoved } from './Home';
import { navItems } from './navigation';

const implemented: Record<string, ReactNode> = {
  '/overview': (
    <RequirePermission permission="platform.tenants">
      <PlatformDashboardPage />
    </RequirePermission>
  ),
  '/tenants': (
    <RequirePermission permission="platform.tenants">
      <TenantsPage />
    </RequirePermission>
  ),
  '/storage': (
    <RequirePermission permission="platform.storage">
      <StoragePage />
    </RequirePermission>
  ),
  '/media-nodes': (
    <RequirePermission permission="platform.media_nodes">
      <MediaNodesPage />
    </RequirePermission>
  ),
  '/live': (
    <RequirePermission permission="live.view">
      <LivePage />
    </RequirePermission>
  ),
  '/playback': (
    <RequirePermission permission="recordings.view">
      <PlaybackPage />
    </RequirePermission>
  ),
  '/evidence': (
    <RequirePermission permission="recordings.view">
      <EvidencePage />
    </RequirePermission>
  ),
  '/events': (
    <RequirePermission permission="events.view">
      <EventsPage />
    </RequirePermission>
  ),
  '/devices': (
    <RequirePermission permission="devices.read">
      <DevicesPage />
    </RequirePermission>
  ),
  '/mosaics': (
    <RequirePermission permission="live.view">
      <MosaicsPage />
    </RequirePermission>
  ),
  '/clients': (
    <RequirePermission permission="clients.read">
      <ClientsPage />
    </RequirePermission>
  ),
  '/audit': (
    <RequirePermission permission="audit.read">
      <AuditPage />
    </RequirePermission>
  ),
  '/settings': (
    <RequirePermission permission="tenant.manage">
      <SettingsPage />
    </RequirePermission>
  ),
  '/integrations': (
    <RequirePermission permission="integrations.manage">
      <IntegrationsPage />
    </RequirePermission>
  ),
  '/users': (
    <RequirePermission permission="users.read">
      <UsersPage />
    </RequirePermission>
  ),
  '/roles': (
    <RequirePermission permission="roles.read">
      <RolesPage />
    </RequirePermission>
  ),
};

export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  // Without a session (ADR-029).
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/accept-invite', element: <AcceptInvitePage /> },
  {
    element: <RequireAuth />,
    children: [
      // One live socket for every authenticated screen, shell or not (ADR-016/039).
      {
        element: (
          <LiveEventsProvider>
            <Outlet />
          </LiveEventsProvider>
        ),
        children: [
          // A mosaic alone in its own window: no shell around the grid.
          {
            path: 'mosaico/:id',
            element: (
              <NotOnPlatform>
                <RequirePermission permission="live.view">
                  <MosaicWindowPage />
                </RequirePermission>
              </NotOnPlatform>
            ),
          },
          // The monitoring panel: shared mosaics + viewer, no shell; opened from the
          // Operação menu (0.68.1) or as the operators' start page.
          {
            path: 'monitoring',
            element: (
              <NotOnPlatform>
                <RequirePermission permission="monitoring.view">
                  <MonitoringPage />
                </RequirePermission>
              </NotOnPlatform>
            ),
          },
          {
            element: <AppShell />,
            children: [
              { index: true, element: <Home /> },
              ...navItems.map((item) => {
                const element = implemented[item.to] ?? <ModulePage item={item} />;
                return {
                  path: item.to,
                  element:
                    item.platform === 'never' ? <NotOnPlatform>{element}</NotOnPlatform> : element,
                };
              }),
              // A media node's page (ADR-051), under the list's menu entry.
              {
                path: 'media-nodes/:id',
                element: (
                  <RequirePermission permission="platform.media_nodes">
                    <MediaNodePage />
                  </RequirePermission>
                ),
              },
              // The "Sistema" screen became a section of the platform's "Painel" (0.67.0).
              { path: 'system', element: <SystemMoved /> },
              // Accounts (former "Locais") now live under their clients (ADR-026).
              { path: 'sites', element: <Navigate to="/clients" replace /> },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
];
